import { message } from 'antd'
import { MessageInstance } from 'antd/es/message/interface'
import { createAccessibleMessageApi } from 'features/assetDatabase/utils/createAccessibleMessageApi'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type AccessibleMessageApiContextType = {
  messageApi: MessageInstance
}

const AccessibleMessageApiContext =
  createContext<AccessibleMessageApiContextType | null>(null)

export const useAccessibleMessageApi = (): MessageInstance => {
  const context = useContext(AccessibleMessageApiContext)
  if (!context) {
    throw new Error(
      'useAccessibleMessageApi must be used within AccessibleMessageApiLayer'
    )
  }
  return context.messageApi
}

export const AccessibleMessageApiLayer: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [messageApi, contextHolder] = message.useMessage()
  const [statusAnnouncement, setStatusAnnouncement] = useState('')
  const [alertAnnouncement, setAlertAnnouncement] = useState('')
  const announce = useCallback(
    (messageText: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (priority === 'assertive') {
        setAlertAnnouncement('')
        requestAnimationFrame(() => setAlertAnnouncement(messageText))
        return
      }

      setStatusAnnouncement('')
      requestAnimationFrame(() => setStatusAnnouncement(messageText))
    },
    []
  )
  const accessibleMessageApi = useMemo(
    () => createAccessibleMessageApi(messageApi, announce),
    [messageApi, announce]
  )

  return (
    <>
      {contextHolder}
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='sr-only' // eslint-disable-line no-classname/no-classname-outside-designsystem
      >
        {statusAnnouncement}
      </div>
      <div
        role='alert'
        aria-live='assertive'
        aria-atomic='true'
        className='sr-only' // eslint-disable-line no-classname/no-classname-outside-designsystem
      >
        {alertAnnouncement}
      </div>
      <AccessibleMessageApiContext.Provider
        value={{ messageApi: accessibleMessageApi }}>
        {children}
      </AccessibleMessageApiContext.Provider>
    </>
  )
}
